/**
 * Validates if the report filters are complete and ready to be sent to the backend.
 *
 * Filter structure:
 * - Logic group: ['and'|'or', condition1, condition2, ...]
 * - Condition: [field, operator, value]
 */

export function isFilterComplete(filter) {
  if (!filter) return true; // null/undefined filters are considered "complete" (none)
  if (!Array.isArray(filter)) return true; // Should be an array, but if not, we don't want to block it

  // If first element is a logic operator, it's a group
  if (filter[0] === 'and' || filter[0] === 'or') {
    const conditions = filter.slice(1);
    // If it's just ['and'], it's incomplete or empty.
    // However, if we initialized it as ['and', [null, null, null]],
    // we need to check all conditions.
    if (conditions.length === 0) return true;
    return conditions.every(isFilterComplete);
  }

  // It's a condition triple [field, operator, value]
  const field = filter[0];
  const operator = filter[1];
  const value = filter[2];

  // field and operator must not be null
  if (field === null || operator === null) return false;

  // Handle 'in' and 'not in' operators - value must be a non-empty array
  if (operator === 'in' || operator === 'not in') {
    return Array.isArray(value) && value.length > 0;
  }

  return true;
}

const isEventIdEmpty = (id) => {
  if (id === null || id === undefined || id === '') return true;
  if (Array.isArray(id) && id.length === 0) return true;
  return false;
};

// Mirrors Request::PROPERTY_AGGREGATIONS (www/frontend/modules/report/templateBuilder/entities/Request.php) —
// these aggregations are rejected by the backend when agr_field is null.
const PROPERTY_AGGREGATIONS = ['property_sum', 'property_avg', 'property_min', 'property_max', 'property_median'];

/**
 * Returns a list of concrete problems found in the report configuration.
 * Each issue is { code, index?, label? } so callers (constructor UI, report
 * viewer) can point at the exact step that's broken instead of just saying
 * "something is wrong".
 */
export function getConfigIssues(config) {
  const issues = [];

  if (!config || !config.source_request || !config.source_request.type) {
    issues.push({ code: 'missingType' });
    return issues;
  }

  const sr = config.source_request;

  // SQL mode: запрос пишет пользователь, проверять нечего кроме его наличия.
  // Маппинг колонок на оси подставляется автоматически после выполнения.
  if (sr.type === 'sql') {
    if (!sr.sql || !String(sr.sql).trim()) {
      issues.push({ code: 'sqlEmpty' });
    }
    return issues;
  }

  // Check top-level filters
  if (sr.filters && !isFilterComplete(sr.filters)) {
    issues.push({ code: 'topLevelFilters' });
  }

  // Check requests (Trends, Funnels, UTM) — per-step filters
  if (Array.isArray(sr.requests)) {
    sr.requests.forEach((req, i) => {
      if (req.filters && !isFilterComplete(req.filters)) {
        issues.push({ code: 'stepFilters', index: i, label: req.label });
      }
      if (PROPERTY_AGGREGATIONS.includes(req.agr) && !req.agr_field) {
        issues.push({ code: 'stepMissingAgrField', index: i, label: req.label });
      }
    });
  }

  // Check Funnels — every step must have an event_id (scalar or non-empty array)
  if (config.type === 'funnels') {
    if (!Array.isArray(sr.requests) || sr.requests.length === 0) {
      issues.push({ code: 'funnelNoSteps' });
    } else {
      sr.requests.forEach((r, i) => {
        if (isEventIdEmpty(r.event_id)) {
          issues.push({ code: 'stepNoEvent', index: i, label: r.label });
        }
      });
    }
  }

  // Check UTM — need at least one request with event_id
  if (config.type === 'utm') {
    if (!Array.isArray(sr.requests) || sr.requests.length === 0) {
      issues.push({ code: 'utmNoRequests' });
    } else if (!sr.requests.some(r => !isEventIdEmpty(r.event_id))) {
      issues.push({ code: 'utmNoEvent' });
    }
  }

  // Check retention
  if (config.type === 'retention') {
    if (sr.target_entity && sr.target_entity.filters && !isFilterComplete(sr.target_entity.filters)) {
      issues.push({ code: 'retentionTargetFilters' });
    }
    if (sr.returning_entity && sr.returning_entity.filters && !isFilterComplete(sr.returning_entity.filters)) {
      issues.push({ code: 'retentionReturningFilters' });
    }
  }

  return issues;
}

/**
 * Validates the entire report configuration for completeness.
 */
export function isReportConfigComplete(config) {
  return getConfigIssues(config).length === 0;
}
